import {
  BookOutlined, BulbOutlined, DeleteOutlined, EditOutlined, GlobalOutlined, LogoutOutlined,
  MenuOutlined, MessageOutlined, MoonOutlined, PaperClipOutlined, PlusOutlined, QuestionCircleOutlined,
  SunOutlined, UserOutlined, WechatWorkOutlined
} from "@ant-design/icons";
import { CustomerServiceOutlined } from "@ant-design/icons";
import {
  App, Button, Col, Divider, Drawer, Form, Grid, Input, Layout, List, Modal,
  Row, Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from "antd";
import {
  collection, doc, getDocs, onSnapshot, query, Timestamp, Unsubscribe,
  where
} from "firebase/firestore";
import React, { useEffect, useRef, useState, useTransition } from "react"; // ⬅️ useRef
import { useDispatch, useSelector } from "react-redux";
import { createChatRoom, deleteChatRoom, getUser, subscribeChatRooms } from "../../apis/apis";
import { auth, db } from "../../config/firebase";
import { mouDarkLogo, mouLightLogo } from "../../images/AppImages";
import { AIProvider, ChatRoom, User } from "../../models/models";
import { setMenuSelection } from "../../redux/slices/menuSelectionSlice";
import { changePromptMasterState } from "../../redux/slices/promptMasterSlice";
import { setPromptData } from "../../redux/slices/promptSelectionSlice";
import { setPromptSendMessageData } from "../../redux/slices/promptSendMessageSlice";
import { toggleTheme } from "../../redux/slices/themeSlice";
import { RootState } from "../../redux/store";
import { convertTimestampToMillis } from "../../utils/functions";
import { storage } from "../../config/firebase";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import {useTranslation} from "react-i18next"


const appName = process.env.REACT_APP_APP_NAME;
const { Sider } = Layout;
const { Text, Title } = Typography;

let isCreatingChat = false;
let promptDataCache: any | null = null;
let lastMasterState = 0;

const LeftMenuSlider: React.FC = () => {
  const [form] = Form.useForm();
  const { message, modal } = App.useApp();
  const dispatch = useDispatch();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.lg;
  const clickableStyle = isMobile ? {} : { cursor: "pointer" };

  // Redux
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
  const logoDarkURL = useSelector((state: RootState) => state.logo.logoDarkURL);
  const logoLightURL = useSelector((state: RootState) => state.logo.logoLightURL);
  const promptData = useSelector((state: RootState) => state.promptData);
  const promptMasterState = useSelector((state: RootState) => state.promptMaster);

  const {t, i18n} = useTranslation()

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  // State
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [selectedChatRoom, setSelectedChatRoom] = useState<string | null>(null);
  const [selectedMenuButton, setSelectedMenuButton] = useState<string | null>("chat");
  const [selectedChatButton, setSelectedChatButton] = useState<string | null>("chat");
  const [loading, setLoading] = useState(false);
  const [promptMaster, setPromptMaster] = useState<any>(null);
  const [isModalPromptMasterVisible, setIsModalPromptMasterVisible] = useState(false);
  const [curretUser, setCurrentUser] = useState<User | null>(null);
  const [aiModels, setAiModels] = useState<any[]>([]);
  const [model, setModel] = useState<string | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const chatRoomsUnsubRef = useRef<Unsubscribe | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pmFiles, setPmFiles] = useState<File[]>([]);
  const removePmFile = (idx: number) => setPmFiles(prev => prev.filter((_, i) => i !== idx));


  function guessContentTypeFromName(name: string): string {
    const ext = (name.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'csv': return 'text/csv';
      case 'txt': return 'text/plain';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'webp': return 'image/webp';
      case 'json': return 'application/json';
      default: return 'application/octet-stream';
    }
  }

  const handleAttachClick = () => fileInputRef.current?.click();

  const onPmFilesChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = Math.max(0, MAX_FILES - pmFiles.length);
    if (!remaining) return message.warning(t('chat.maxFiles', { max: MAX_FILES }));
    const take = files.slice(0, remaining);
    const tooBig = take.find(f => f.size / (1024 * 1024) > MAX_SIZE_MB);
    if (tooBig) return message.error(t('chat.fileExceeds', { max: MAX_SIZE_MB }));
    setPmFiles(prev => [...prev, ...take]);
  };

  const uploadSingleToChatRoom = async (file: File, chatRoomId: string) => {
    if (!auth.currentUser || !curretUser?.clientId) {
      message.error(t('chat.noSessionOrChat'));
      return null;
    }
    const safeName = file.name.replace(/\s+/g, '_');
    const path = `Clients/${curretUser.clientId}/Users/${auth.currentUser.uid}/ChatRooms/${chatRoomId}/attachments/${Date.now()}_${safeName}`;
    const ref = storageRef(storage, path);
    const ct = file.type || guessContentTypeFromName(file.name);
    try {
      await uploadBytes(ref, file, { contentType: ct });
      const url = await getDownloadURL(ref);
      return { name: file.name, contentType: ct, size: file.size, gcsPath: path, downloadURL: url };
    } catch {
      message.error(`${t('chat.uploadError')} ${file.name}`);
      return null;
    }
  };


  const ACCEPT = ['image/*', '.pdf', '.csv', '.txt', '.docx', '.xls', '.xlsx'].join(',');
  const MAX_FILES = 5;
  const MAX_SIZE_MB = 20;


  // Menu items - Fixed at top
  const fixedMenuButtons = React.useMemo(() => [
    { id: "chat", icon: <WechatWorkOutlined style={{ fontSize: 18 }} />, title: t("common.newChat") },
    { id: "prompts", icon: <EditOutlined style={{ fontSize: 18 }} />, title: t("common.prompts") },
    { id: "prompt-master", icon: <BulbOutlined style={{ fontSize: 18 }} />, title: t("common.promptMaster") },
    { id: "assistants", icon: <UserOutlined style={{ fontSize: 18 }} />, title: t("common.assistants") },
  ], [t]);

  // Menu items - Scrollable
  const scrollableMenuButtons = React.useMemo(() => [
    { id: "academy", icon: <BookOutlined style={{ fontSize: 18 }} />, title: t("common.academy") },
    { id: "faq", icon: <QuestionCircleOutlined style={{ fontSize: 18 }} />, title: t("common.faqs") },
    {
      id: "toggle-theme",
      icon: isDarkMode ? <SunOutlined style={{ fontSize: 18 }} /> : <MoonOutlined style={{ fontSize: 18 }} />,
      title: isDarkMode ? t("common.lightTheme") : t("common.darkTheme"),
    },
    { id: "logout", icon: <LogoutOutlined style={{ fontSize: 18 }} />, title: t("common.logout") },
  ], [t, isDarkMode]);

  // arriba del return del componente
  const firstVarName = promptMaster?.variables?.[0];
  const firstVarLabel = promptMaster?.values?.[0] || t('chat.taskToPerform');

  useEffect(() => {
    if (!firstVarName) return;
    form.validateFields([firstVarName]).catch(() => { });
  }, [pmFiles, firstVarName, form]);


  async function deleteAllTestChats() {
    if (!auth.currentUser) {
      message.error(t('chat.noActiveAccount'));
      return;
    }
    if (!curretUser?.clientId) {
      message.error(t('chat.noClientIdFound'));
      return;
    }
    try {
      setLoading(true);
      const ref = collection(db, `Clients/${curretUser.clientId}/Users/${auth.currentUser.uid}/ChatRooms`);
      const snap = await getDocs(query(ref, where('name', '==', 'TestChat')));
      if (snap.empty) {
        message.info(t('chat.noTestChatsFound'));
        return;
      }
      await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data() as any;
          const chat: ChatRoom = {
            id: d.id,
            name: data.name,
            userId: data.userId ?? auth.currentUser!.uid,
            createdAt: data.createdAt ?? null,
          };
          await deleteChatRoom(chat);
        })
      );
      message.success(t('chat.testChatsDeleted', { count: snap.size }));
    } catch (e) {
      console.error('deleteAllTestChats error:', e);
      message.error(t('chat.couldNotDeleteChats'));
    } finally {
      setLoading(false);
    }
  }



  // monta/desmonta la subscripción cuando ya conoces el clientId
  useEffect(() => {
    if (!auth.currentUser || !curretUser?.clientId) return;
    //deleteAllTestChats();
    getAllChatRooms();
    return () => chatRoomsUnsubRef.current?.();
  }, [curretUser?.clientId]);


  // Effects (…iguales a los tuyos…)
  useEffect(() => { getAllChatRooms(); }, []);
  useEffect(() => { fetchPromptMasters(); }, []);
  useEffect(() => {
    if (promptData.promptId === "") return;
    promptDataCache = promptData;
    setSelectedMenuButton("chat");
    dispatch(setMenuSelection("chat"));
    setDrawerVisible(false);
    autoCreateChatRoom(promptData.chatName);
  }, [promptData]);

  useEffect(() => {
    if (!selectedChatRoom || !promptDataCache) return;
    (async () => {
      let wireAttachments: any[] = [];
      if (pmFiles.length) {
        const uploaded = await Promise.all(pmFiles.map(f => uploadSingleToChatRoom(f, selectedChatRoom)));
        wireAttachments = (uploaded.filter(Boolean) as any[]);
      }
      const sendMessageData: any = {
        promptId: promptDataCache.promptId,
        prompt: promptDataCache.prompt,
        chatRoomId: selectedChatRoom,
        model: promptDataCache.model,
        attachments: wireAttachments
      };
      dispatch(setPromptSendMessageData(sendMessageData));
      promptDataCache = null;
      setPmFiles([]);
    })();
  }, [selectedChatRoom, dispatch]);


  useEffect(() => {
    if (lastMasterState === promptMasterState.stateCounter) return;
    lastMasterState = promptMasterState.stateCounter;
    setIsModalPromptMasterVisible(true);
  }, [promptMasterState]);

  useEffect(() => {
    (async () => { const user = await getUser(); setCurrentUser(user); })();
  }, []);

  useEffect(() => {
    const handleToggleDrawer = () => {
      setDrawerVisible((prev) => !prev);
    };
    window.addEventListener('toggleDrawer', handleToggleDrawer);
    return () => {
      window.removeEventListener('toggleDrawer', handleToggleDrawer);
    };
  }, []);

  useEffect(() => {
    if (!curretUser?.clientId) return;
    const providersRef = collection(db, `Clients/${curretUser.clientId}/AIProviders`);
    const q = query(providersRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const providersData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as AIProvider[];
      if (!providersData.length) {
        message.error(t('chat.noAiModelError'));
        return;
      }
      setAiModels(providersData.map((p) => ({ value: p.name, label: p.name })));
      setModel((prev) => prev || providersData[0].name);
    });
    return () => unsubscribe();
  }, [curretUser, message]);

  function fetchPromptMasters() {
    const promptRef = doc(db, `Prompts/csnOoOImlPt4uY6djxH5`);
    const unsub = onSnapshot(promptRef, (d) => setPromptMaster(d.data()));
    return () => unsub();
  }

  const handleOk = () => {
    form.validateFields().then((values) => {
      let filledPrompt = promptMaster.prompt;
      promptMaster.variables.forEach((variable: any) => {
        const cleanVariable = String(variable).replace(/,/g, "").trim();
        const regex = new RegExp(`@${cleanVariable}`, "g");
        filledPrompt = filledPrompt.replace(regex, values[variable]);
      });
      const chatName = promptMaster.name.length > 23 ? `${promptMaster.name.substring(0, 20)}...` : promptMaster.name;
      dispatch(setPromptData({ promptId: promptMaster.id, prompt: filledPrompt, chatName, model }));
      message.success(t('chat.promptSentCorrectly'));
      handleCancel();
    });
  };
  const handleCancel = () => { setIsModalPromptMasterVisible(false); form.resetFields(); };

  function getMouLogo() { return isDarkMode ? (logoDarkURL || logoLightURL || mouDarkLogo) : (logoLightURL || logoDarkURL || mouLightLogo); }

  function handleLogout() {
    modal.confirm({
      title: t('chat.logoutTitle'),
      content: t('chat.logoutConfirm'),
      onOk: async () => {
        try {
          await auth.signOut();
          localStorage.clear();
          window.location.reload();
        } catch (error) {
          console.error('Logout error:', error);
          message.error(t('chat.logoutError'));
        }
      },
    });
  }


  function handleMenuButton(menuButtonId: string) {
    setSelectedMenuButton(menuButtonId);
    dispatch(setMenuSelection(menuButtonId));
    setDrawerVisible(false);
    if (menuButtonId === "chat") {
      setSelectedChatRoom(null);
      dispatch({
        type: "chatRoom/setChatRoom",
        payload: { id: "", name: "", userId: "", createdAt: null },
      });
    }
  }



  async function handleNewChatByName(chatName?: string) {
    if (isCreatingChat) return;
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    const name = (chatName ?? "").trim();
    if (!name) return message.error(t('chat.chatNameRequired'));
    const newChat: ChatRoom = { id: "", name, userId: auth.currentUser.uid, createdAt: null };
    setLoading(true); isCreatingChat = true;
    try {
      const chatRoomId = await createChatRoom(newChat);
      if (chatRoomId) { newChat.id = chatRoomId; await getAllChatRooms(); handleSelectChat(newChat); }
    } finally { isCreatingChat = false; setLoading(false); }
  }

  async function autoCreateChatRoom(chatName: string) {
    if (isCreatingChat) return message.warning(t('chat.creatingChatWait'));
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));

    await handleNewChatByName(chatName);
  }


  async function getAllChatRooms() {
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    if (!curretUser?.clientId) return;

    setLoading(true);

    // corta una subscripción previa (si la hubiera)
    chatRoomsUnsubRef.current?.();

    chatRoomsUnsubRef.current = subscribeChatRooms(
      curretUser.clientId,
      auth.currentUser.uid,
      (rooms) => {
        setChatRooms(rooms);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }


  function handleSelectChat(chat: ChatRoom) {
    setSelectedChatRoom(chat.id);
    dispatch({ type: "chatRoom/setChatRoom", payload: { ...chat, createdAt: convertTimestampToMillis(chat.createdAt as Timestamp) } });
    setDrawerVisible(false);
  }

  async function handleDeleteChat(chatRoom: ChatRoom) {
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    modal.confirm({
      title: t('chat.deleteChat'),
      content: t('chat.deleteChatConfirm'),
      async onOk() {
        const result = await deleteChatRoom(chatRoom);
        if (result) {
          if (chatRoom.id === selectedChatRoom) {
            dispatch({ type: 'chatRoom/setChatRoom', payload: { id: '', name: '', userId: '', createdAt: null } });
          }
          setSelectedChatRoom(null);
        }
      },
    });
  }


  function handleModelChange(value: string) { setModel(value); }


  const getButtonStyle = (optionId: string | null, selectedOption: string | null) => {
    if (optionId === selectedOption) {
      return {
        backgroundColor: "#e5f6f8",
        padding: "8px 5px",
        color: "#5393cc",
        borderRadius: "6px",
        marginBottom: "5px",
        ...clickableStyle,
      };
    } else {
      return {
        padding: "8px 5px",
        marginBottom: "5px",
        ...clickableStyle,
      };
    }
  }
  const handleMenuItemClick = (item: any) => {
  if (item.id === "prompt-master") {
    setSelectedChatRoom(null);
    dispatch({ type: "chatRoom/setChatRoom", payload: { id: "", name: "", userId: "", createdAt: null } });
    setSelectedMenuButton("prompt-master");
    dispatch(setMenuSelection("prompt-master"));
    setDrawerVisible(false);
    return;
  }
  if (item.id === "toggle-theme") {
    dispatch(toggleTheme());
    return;
  }
  if (item.id === "logout") {
    handleLogout();
    return;
  }
  handleMenuButton(item.id);
};


  const listItemA11y = {
    role: "button" as const,
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        const target = (e.currentTarget as HTMLElement).dataset.itemid;
        if (!target) return;
      }
    },
  };

  // Fixed menu items list
  const FixedMenuButtonsList = (
    <List
      split={false}
      dataSource={fixedMenuButtons}
      renderItem={(item: any) => (
        <List.Item
          key={item.id}
          style={getButtonStyle(item.id, selectedMenuButton)}
          onClick={() => handleMenuItemClick(item)}
          {...(!isMobile ? listItemA11y : {})}
        >
          <List.Item.Meta
            avatar={item.icon}
            title={
              <Text
                style={{
                  fontWeight: selectedMenuButton === item.id ? 600 : 400,
                  color: selectedMenuButton === item.id ? "#5393cc" : undefined,
                }}
              >
                {item.title}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );

  const scrollableData = React.useMemo(() => {
    const buttons = [...scrollableMenuButtons];
    const logoutIndex = buttons.findIndex(b => b.id === 'logout');
    const supportItem = { id: 'support', icon: <CustomerServiceOutlined style={{ fontSize: 18 }} />, title: t("common.support") } as any;
    if (logoutIndex >= 0) {
      buttons.splice(logoutIndex, 0, supportItem);
    } else {
      buttons.push(supportItem);
    }
    return buttons;
  }, [scrollableMenuButtons, t]);

  // Scrollable menu items list
  const ScrollableMenuButtonsList = (
    <List
      split={false}
      dataSource={scrollableData}
      renderItem={(item: any) => (
        <List.Item
          key={item.id}
          style={getButtonStyle(item.id, selectedMenuButton)}
          onClick={() => handleMenuItemClick(item)}
          {...(!isMobile ? listItemA11y : {})}
        >
          <List.Item.Meta
            avatar={item.icon}
            title={
              <Text
                style={{
                  fontWeight: selectedMenuButton === item.id ? 600 : 400,
                  color: selectedMenuButton === item.id ? "#5393cc" : undefined,
                }}
              >
                {item.title}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );


  const ChatHistoryList = (
    <List
      split={false}
      dataSource={chatRooms}
      renderItem={(chat: ChatRoom) => (
        <List.Item
          key={chat.id}
          style={getButtonStyle(chat.id, selectedChatRoom)}
          onClick={() => handleSelectChat(chat)}
          {...(!isMobile ? listItemA11y : {})}
          actions={[
            <DeleteOutlined
              key="delete"
              style={{
                fontWeight: selectedChatRoom === chat.id ? 600 : 400,
                color: selectedChatRoom === chat.id ? "#5393cc" : undefined,
                fontSize: 16,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteChat(chat);
              }}
            />,
          ]}
        >
          <List.Item.Meta
            avatar={<MessageOutlined style={{ fontSize: 16 }} />}
            title={
              <Text
                style={{
                  fontWeight: selectedChatRoom === chat.id ? 600 : 400,
                  color: selectedChatRoom === chat.id ? "#5393cc" : undefined,
                }}
              >
                {chat.name}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );

  const HeaderLogo = (
    <Row justify="center">
      <Col span={24} style={{ textAlign: "center" }}>
        <img src={getMouLogo()} alt={appName} style={{ width: 120, height: "auto", display: "inline-block" }} />
      </Col>
    </Row>
  );


  const SiderContent = (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: 16, paddingBottom: 0, flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        {!isMobile && HeaderLogo}

        {!isMobile }

        {/* Fixed menu items */}
        {FixedMenuButtonsList}

        {/* Scrollable section */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            minHeight: 0,
          }}
        >
          {ScrollableMenuButtonsList}

          {selectedMenuButton === "chat" && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Row gutter={[0, 12]}>
                <Col span={24}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('chat.history')}
                  </Text>
                </Col>
                <Col span={24}>{ChatHistoryList}</Col>
              </Row>
            </>
          )}
        </div>
      </div>

      {/* Fixed bottom section - User Profile */}
      <div
        style={{
          borderTop: "1px solid",
          borderColor: isDarkMode
            ? "rgba(255, 255, 255, 0.12)"
            : "rgba(0, 0, 0, 0.06)",
          padding: 16,
          flexShrink: 0,
        }}
      >
        

        <div
          style={{
            background: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#f5f5f5",
            borderRadius: 8,
            padding: 12,
          }}
        >
          <Row align="middle" gutter={12}>
            <div
              style={{
                height: "36px",
                width: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "10px",
                borderRadius: "50%",
                backgroundColor: "#e2f4ff",
              }}
            >
              <UserOutlined style={{ fontSize: 20, color: "#1890ff" }} />
            </div>

            <Col flex="auto">
              <div>
                {/* <Text strong style={{ display: "block", fontSize: 14 }}>
                  {curretUser?.name || t('chat.user')}
                </Text> */}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {curretUser?.phone || ""}
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );



  return (
    <>
      <Spin spinning={loading} size="large">
        {!isMobile && (
          <Sider
            width={260}
            collapsedWidth={0}
            style={{
              height: "100vh",
              overflow: "hidden",
              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "white",
              //zIndex: 1000000000
              // borderRight: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.06)"}`,
            }}
          >
            {SiderContent}
          </Sider>
        )}

        <Drawer
        
          title={
            <div style={{ width: '100%', display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
              <img src={getMouLogo()} alt={appName} style={{ height: 24 }} />
            </div>
          }
          width="75%"
          placement="left"
          closable={false}
          onClose={() => setDrawerVisible(false)}
          open={isMobile && drawerVisible}
          styles={{ header: { display: 'flex', alignItems: 'center' }, body: { padding: 0, overflow: 'hidden', height: '100%' } }}
        >
          {SiderContent}
        </Drawer>


       
      </Spin>

      <Modal
        title={<Title level={4} style={{ margin: 0 }}>{promptMaster?.name}</Title>}
        open={isModalPromptMasterVisible}
        onOk={handleOk}
        onCancel={() => { form.resetFields(); handleCancel(); }}
        okText={t('prompts.accept')}
        cancelText={t('common.cancel')}
        styles={{ header: { padding: isMobile ? 16 : 24 }, body: { padding: isMobile ? 16 : 24 } }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {promptMaster?.description}
        </Typography.Paragraph>
        <Form
          form={form}
          layout="vertical"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleOk(); } }}
        >
          {promptMaster?.variables.map((variable: any, index: number) => {
            return (
              <Form.Item
                key={0}
                label={promptMaster?.values[0]}
                name={promptMaster?.variables[0]}
                rules={[
                  {
                    validator: async (_, _value) => {
                      const v = String(_value ?? "").trim();
                      if (v || pmFiles.length > 0) return;
                      throw new Error(t('chat.enterOrAttachFile', { field: promptMaster?.values?.[0] || t('chat.taskToPerform') }));
                    },
                  },
                ]}
              >
                <>
                  <Row gutter={8} wrap={false} align="middle">
                    <Col flex="none">
                      <Tooltip title="Adjuntar archivos">
                        <Button icon={<PlusOutlined />} onClick={() => fileInputRef.current?.click()} />
                      </Tooltip>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        style={{ display: "none" }}
                        onChange={onPmFilesChosen}
                      />
                    </Col>
                    <Col flex="auto">
                      <Input placeholder={promptMaster?.placeholders?.[0]} />
                    </Col>
                  </Row>

                  {pmFiles.length > 0 && (
                    <Row gutter={[6, 6]} wrap style={{ marginTop: 8 }}>
                      {pmFiles.map((f, idx) => (
                        <Col key={`${f.name}_${idx}`}>
                          <Tag
                            closable
                            onClose={(e) => {
                              e.preventDefault();
                              removePmFile(idx);
                            }}
                            closeIcon={<DeleteOutlined />}
                          >
                            <Space size={6} align="center">
                              <PaperClipOutlined />
                              <span style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {f.name}
                              </span>
                            </Space>
                          </Tag>
                        </Col>
                      ))}
                    </Row>
                  )}
                </>
              </Form.Item>

            );
          })}



          <Form.Item
            key="model"
            label={t('prompts.aiModel')}
            name="ai_model"
            rules={[{ required: true, message: t('prompts.pleaseSelectAiModel') }]}
          >
            <Select
              value={model}
              onChange={handleModelChange}
              options={aiModels}
              placeholder={t('prompts.aiModel')}
              style={{ minWidth: 200 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LeftMenuSlider;
